import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { nutztFunktion } from "@/office/excel/formeln";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { formulaCells, readWorkbook } from "@/office/excel/workbook";
import { MAKRO_GLIEDERUNG } from "../makros";

export const lektionGliederung: Lesson = {
  id: "gliederung",
  title: "Gruppieren, Gliederung und Teilergebnisse",
  summary: "Details ein- und ausklappen, Zwischensummen je Gruppe – mit zwei Klicks.",
  durationMinutes: 11,
  learningGoals: [
    "Zeilen gruppieren und über die Gliederungssymbole ein- und ausblenden",
    "Mit Daten → Teilergebnis Zwischensummen je Gruppe erzeugen",
    "Den Unterschied zwischen Gruppieren und Ausblenden kennen",
  ],
  steps: [
    {
      id: "gruppieren",
      title: "Zeilen gruppieren",
      explanation: [
        "„Daten“ → „Gruppieren“ (Umschalt + Alt + Pfeil rechts) fasst markierte Zeilen " +
          "zusammen. Links am Rand erscheinen eine Klammer mit Minuszeichen und oben die " +
          "Ebenenschalter 1 und 2. Ein Klick auf das Minus klappt die Gruppe zu, auf die 1 " +
          "werden alle Gruppen eingeklappt.",
        "Anders als beim Ausblenden ist sofort sichtbar, dass hier etwas zugeklappt ist – " +
          "und jede Person kann es mit einem Klick wieder öffnen. Für Monatsdetails unter " +
          "einer Quartalssumme ist das die richtige Wahl.",
      ],
      keyPoints: [
        "Daten → Gruppieren, Umschalt + Alt + Pfeil rechts.",
        "Ebenenschalter 1, 2, 3 am linken Rand.",
        "Gruppieren statt ausblenden – es bleibt sichtbar, dass etwas fehlt.",
      ],
      task: {
        instruction: "Gruppiere mindestens zwei Zeilen und klappe die Gruppe einmal zu und wieder auf.",
        hint: "Zeilen markieren (Zeilenköpfe), Daten → Gruppieren.",
        macro: MAKRO_GLIEDERUNG,
        check: () =>
          pruefeMitMakro(MAKRO_GLIEDERUNG, (bericht) => {
            const blaetter = [...bericht.gruppe("blatt").values()];
            const gruppiert = blaetter.filter((blatt) => Number(blatt.get("gruppierteZeilen") ?? 0) >= 2);
            return fromCriteria(
              [
                criterion(
                  gruppiert[0]
                    ? `Auf „${gruppiert[0].get("name")}“ sind ${gruppiert[0].get("gruppierteZeilen")} Zeilen gruppiert`
                    : "Mindestens zwei Zeilen sind gruppiert",
                  gruppiert.length > 0,
                ),
              ],
              {
                success: "Die Gruppe lässt sich jetzt mit einem Klick ein- und ausklappen.",
                failure: "Es sind noch keine Zeilen gruppiert.",
              },
            );
          }),
      },
    },
    {
      id: "teilergebnisse",
      title: "Teilergebnisse",
      explanation: [
        "„Daten“ → „Teilergebnis“ fügt nach jeder Gruppe einer sortierten Liste eine " +
          "Zwischensumme ein und am Ende ein Gesamtergebnis – samt dreistufiger Gliederung. " +
          "Voraussetzung: Die Liste ist nach der Gruppenspalte sortiert, und sie ist keine " +
          "Tabelle (Strg + T), denn in Tabellen ist der Befehl gesperrt.",
        "Die eingefügten Formeln benutzen TEILERGEBNIS(9;…). Die 9 steht für Summe, und " +
          "TEILERGEBNIS überspringt andere Teilergebnisse – deshalb zählt das Gesamtergebnis " +
          "nichts doppelt.",
        "Wieder entfernen lässt sich alles über denselben Dialog: „Alle entfernen“.",
      ],
      keyPoints: [
        "Erst nach der Gruppenspalte sortieren, dann Daten → Teilergebnis.",
        "Nicht in einer Tabelle – dort ist der Befehl gesperrt.",
        "Ebene 2 zeigt nur die Zwischensummen.",
      ],
      task: {
        instruction:
          "Sortiere eine Liste nach einer Gruppenspalte (etwa Region) und füge über " +
          "„Daten“ → „Teilergebnis“ Zwischensummen je Gruppe ein.",
        hint: "Sortieren, dann Daten → Teilergebnis → „Gruppieren nach“ Region, „Unter Verwendung von“ Summe.",
        macro: MAKRO_GLIEDERUNG,
        check: async () => {
          const mappe = await readWorkbook();
          const teilergebnisse = formulaCells(mappe).filter((zelle) => nutztFunktion(zelle.formula, "teilergebnis"));
          return pruefeMitMakro(MAKRO_GLIEDERUNG, (bericht) => {
            const ebenen = Math.max(1, ...[...bericht.gruppe("blatt").values()].map((blatt) => Number(blatt.get("ebenen") ?? 1)));
            return fromCriteria(
              [
                criterion(`Mindestens zwei TEILERGEBNIS-Formeln (gefunden: ${teilergebnisse.length})`, teilergebnisse.length >= 2),
                criterion(`Eine Gliederung mit mindestens drei Ebenen (gefunden: ${ebenen})`, ebenen >= 3),
              ],
              {
                success: "Zwischensummen je Gruppe, Gesamtergebnis und Gliederung – in einem Schritt.",
                failure: "Die Teilergebnisse fehlen noch.",
                hint: "Ist der Befehl ausgegraut, liegt die Liste in einer Tabelle: Tabellentools → In Bereich konvertieren.",
              },
            );
          });
        },
      },
    },
  ],
};
